/**
 * Step 6: Afspraken & Afronden
 *
 * Add agreements and finalize the evaluation.
 */

import { useState, useEffect } from 'react';
import {
  useEvaluation,
  useAgreements,
  useAddAgreement,
  useDeleteAgreement,
  useUpdateEvaluationStatus,
} from '../useEvaluations';
import { useWizard } from './WizardContext';
import type { AgreementType, AgreementInput } from '../../../lib/evaluationsApi';

interface Step6AgreementsProps {
  evaluationId: number;
  onFinalize?: () => void;
}

/**
 * Assessment Report Section Component
 * Allows uploading transcript before generating the report
 */
function AssessmentReportSection({ evaluationId }: { evaluationId: number }) {
  const [transcript, setTranscript] = useState('');
  const [showTranscriptInput, setShowTranscriptInput] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setTranscript(content);
      setShowTranscriptInput(true);
    };
    reader.readAsText(file);
  };

  const handleGenerateReport = () => {
    const params = new URLSearchParams();
    if (transcript.trim()) {
      // Store transcript in sessionStorage to pass to the report page
      sessionStorage.setItem(`assessment-transcript-${evaluationId}`, transcript);
      params.set('hasTranscript', 'true');
    }
    const url = `/beoordeling/${evaluationId}/rapport${params.toString() ? `?${params.toString()}` : ''}`;
    window.open(url, '_blank');
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <h3 className="text-lg font-medium text-blue-800 mb-2">Beoordelingsverslag Genereren</h3>
      <p className="text-sm text-blue-700 mb-4">
        Genereer een AI-samenvatting van de beoordeling met de structuur: wat ging goed,
        wat ging minder, en gemaakte afspraken. Het verslag kan direct worden afgedrukt of als PDF opgeslagen.
      </p>

      {/* Transcript Input Section */}
      <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900">Transcriptie / Input Document (optioneel)</h4>
            <p className="text-xs text-gray-600 mt-1">
              Upload een transcriptie of gespreksnotities om het verslag te verrijken met extra context.
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".txt,.md,.doc,.docx,.csv"
              onChange={handleFileUpload}
              className="hidden"
            />
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-700 text-sm rounded-lg hover:bg-purple-200 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Bestand Uploaden
            </span>
          </label>
          <button
            onClick={() => setShowTranscriptInput(!showTranscriptInput)}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {showTranscriptInput ? 'Verbergen' : 'Tekst Invoeren'}
          </button>
          {transcript && (
            <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Transcriptie toegevoegd
            </span>
          )}
        </div>

        {showTranscriptInput && (
          <div className="mt-3">
            <textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              placeholder="Plak hier de transcriptie, gespreksnotities of andere relevante informatie voor het beoordelingsverslag..."
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => {
                  setTranscript('');
                  setShowTranscriptInput(false);
                }}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
              >
                Wissen
              </button>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleGenerateReport}
        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Verslag Genereren
      </button>
    </div>
  );
}

interface ExtendedAgreementInput extends AgreementInput {
  isEmployeeActionHolder?: boolean;
}

const AGREEMENT_TYPES: { value: AgreementType; label: string }[] = [
  { value: 'development', label: 'Ontwikkeling' },
  { value: 'training', label: 'Training/Opleiding' },
  { value: 'target', label: 'Doelstelling' },
  { value: 'other', label: 'Overig' },
];

export function Step6Agreements({ evaluationId, onFinalize }: Step6AgreementsProps) {
  const { data: evaluation, isLoading: isLoadingEval } = useEvaluation(evaluationId);
  const { data: agreementsData, isLoading: isLoadingAgreements } = useAgreements(evaluationId);
  const addAgreement = useAddAgreement();
  const deleteAgreement = useDeleteAgreement();
  const updateStatus = useUpdateEvaluationStatus();
  const { prevStep, stepData, updateStepData } = useWizard();

  const [showForm, setShowForm] = useState(false);
  const [newAgreement, setNewAgreement] = useState<ExtendedAgreementInput>({
    type: 'development',
    beschrijving: '',
    deadline: undefined,
    isEmployeeActionHolder: false,
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [opmerkingen, setOpmerkingen] = useState(stepData.step6?.opmerkingen || '');

  // Sync opmerkingen from wizard state when component loads
  useEffect(() => {
    if (stepData.step6?.opmerkingen !== undefined) {
      setOpmerkingen(stepData.step6.opmerkingen);
    }
  }, [stepData.step6?.opmerkingen]);

  // Update wizard state when opmerkingen changes (for auto-save)
  const handleOpmerkingenChange = (value: string) => {
    setOpmerkingen(value);
    updateStepData('step6', {
      ...stepData.step6,
      agreements: stepData.step6?.agreements || [],
      opmerkingen: value,
    });
  };

  const handleAddAgreement = async () => {
    if (!newAgreement.beschrijving.trim()) return;

    await addAgreement.mutateAsync({
      evaluationId,
      input: newAgreement,
    });

    setNewAgreement({ type: 'development', beschrijving: '', deadline: undefined, isEmployeeActionHolder: false });
    setShowForm(false);
  };

  const handleDeleteAgreement = async (agreementId: number) => {
    if (window.confirm('Weet je zeker dat je deze afspraak wilt verwijderen?')) {
      await deleteAgreement.mutateAsync({ evaluationId, agreementId });
    }
  };

  const handleFinalize = async () => {
    try {
      // Send opmerkingen along with status update to persist to evaluation record
      await updateStatus.mutateAsync({
        id: evaluationId,
        status: 'submitted',
        opmerkingen: opmerkingen || undefined,
      });
      setShowConfirm(false);
      onFinalize?.();
    } catch {
      // Error handled by mutation
    }
  };

  const isLoading = isLoadingEval || isLoadingAgreements;
  const agreements = agreementsData?.agreements || evaluation?.agreements || [];
  // Can only submit from draft status - all other statuses are considered finalized/not editable
  const isFinalized = evaluation?.status !== 'draft';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Afspraken laden...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Stap 6: Afspraken & Afronden</h2>
        <p className="mt-1 text-sm text-gray-500">
          Leg afspraken vast en rond de beoordeling af.
        </p>
      </div>

      {/* Agreements List */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Afspraken ({agreements.length})</h3>
          {!isFinalized && !showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Afspraak Toevoegen
            </button>
          )}
        </div>

        {/* Add Agreement Form */}
        {showForm && (
          <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={newAgreement.type}
                    onChange={(e) => setNewAgreement((prev) => ({ ...prev, type: e.target.value as AgreementType }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {AGREEMENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deadline (optioneel)</label>
                  <input
                    type="date"
                    value={newAgreement.deadline || ''}
                    onChange={(e) => setNewAgreement((prev) => ({ ...prev, deadline: e.target.value || undefined }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                <textarea
                  rows={3}
                  value={newAgreement.beschrijving}
                  onChange={(e) => setNewAgreement((prev) => ({ ...prev, beschrijving: e.target.value }))}
                  placeholder="Beschrijf de afspraak..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <input
                  type="checkbox"
                  id="isEmployeeActionHolder"
                  checked={newAgreement.isEmployeeActionHolder || false}
                  onChange={(e) => setNewAgreement((prev) => ({ ...prev, isEmployeeActionHolder: e.target.checked }))}
                  className="mt-1 h-4 w-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
                />
                <label htmlFor="isEmployeeActionHolder" className="flex-1 cursor-pointer">
                  <span className="block text-sm font-medium text-amber-800">Medewerker is actiehouder</span>
                  <span className="block text-xs text-amber-600 mt-0.5">
                    De medewerker is verantwoordelijk voor opvolging van deze afspraak binnen de deadline.
                    De afspraak wordt getoond op het dashboard van de medewerker.
                  </span>
                </label>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleAddAgreement}
                  disabled={!newAgreement.beschrijving.trim() || addAgreement.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {addAgreement.isPending ? 'Toevoegen...' : 'Toevoegen'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Agreements List */}
        <div className="divide-y divide-gray-200">
          {agreements.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                Nog geen afspraken toegevoegd.
              </p>
            </div>
          ) : (
            agreements.map((agreement) => (
              <div key={agreement.id} className="px-6 py-4 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      agreement.type === 'development' ? 'bg-blue-100 text-blue-700' :
                      agreement.type === 'training' ? 'bg-purple-100 text-purple-700' :
                      agreement.type === 'target' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {AGREEMENT_TYPES.find((t) => t.value === agreement.type)?.label || agreement.type}
                    </span>
                    {agreement.isEmployeeActionHolder && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        Medewerker actiehouder
                      </span>
                    )}
                    {agreement.deadline && (
                      <span className="text-xs text-gray-500">
                        Deadline: {new Date(agreement.deadline).toLocaleDateString('nl-NL')}
                      </span>
                    )}
                    {agreement.isCompleted && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                        Afgerond
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-900">{agreement.beschrijving}</p>
                </div>
                {!isFinalized && (
                  <button
                    onClick={() => handleDeleteAgreement(agreement.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title="Verwijderen"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Opmerkingen Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Opmerkingen (optioneel)</h3>
          <p className="mt-1 text-sm text-gray-500">
            Algemene opmerkingen of conclusies over de beoordeling als geheel.
          </p>
        </div>
        <div className="p-6">
          <textarea
            rows={4}
            value={opmerkingen}
            onChange={(e) => handleOpmerkingenChange(e.target.value)}
            placeholder="Vul hier eventuele algemene opmerkingen in over de beoordeling..."
            disabled={isFinalized}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              isFinalized ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
          />
          <p className="mt-2 text-xs text-gray-500">
            Deze opmerkingen worden opgeslagen bij de beoordeling en zijn zichtbaar voor HR.
          </p>
        </div>
      </div>

      {/* Assessment Report Section */}
      <AssessmentReportSection evaluationId={evaluationId} />

      {/* Finalize Section */}
      {!isFinalized && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">Beoordeling Afronden</h3>
          <p className="text-sm text-yellow-700 mb-4">
            Na het afronden wordt de beoordeling vergrendeld en doorgestuurd naar HR voor review.
            Zorg dat alle scores en afspraken correct zijn ingevuld.
          </p>
          <button
            onClick={() => setShowConfirm(true)}
            className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Beoordeling Indienen
          </button>
        </div>
      )}

      {isFinalized && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-green-800 mb-2">Beoordeling Ingediend</h3>
          <p className="text-sm text-green-700">
            Deze beoordeling is ingediend en wacht op review door HR.
          </p>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Beoordeling Indienen?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Weet je zeker dat je deze beoordeling wilt indienen? Na het indienen kun je de scores
              niet meer aanpassen.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuleren
              </button>
              <button
                onClick={handleFinalize}
                disabled={updateStatus.isPending}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {updateStatus.isPending ? 'Indienen...' : 'Ja, Indienen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={prevStep}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Vorige
        </button>
        {isFinalized && (
          <button
            onClick={() => onFinalize?.()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Terug naar Overzicht
          </button>
        )}
      </div>
    </div>
  );
}
